// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';
import { SingleTabStopNavigationProvider, } from '@cloudscape-design/component-toolkit/internal';
import InternalToken from '../../../token/internal';
import handleKey from '../../utils/handle-key';
import { nodeBelongs } from '../../utils/node-belongs';
import { getAllFocusables } from '../focus-lock/utils';
import { OverflowDropdown } from './overflow-dropdown';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';
export const InlineTokens = forwardRef(({ tokens, visibleCount, disabled, readOnly, expandToViewport, overflowOpen, tokenDismissLabel, tokenOverflowAriaLabel, onOverflowOpenChange, onDismiss, onFocusEnter, onNavigateToInput, }, ref) => {
    const rootRef = useRef(null);
    const navigationAPI = useRef(null);
    const lastFocusedRef = useRef(null);
    const hiddenCount = tokens.length - visibleCount;
    const hiddenTokens = tokens.slice(0, hiddenCount);
    const visibleTokens = tokens.slice(hiddenCount);
    const getFocusables = () => rootRef.current ? getAllFocusables(rootRef.current).filter(el => { var _a; return (_a = navigationAPI.current) === null || _a === void 0 ? void 0 : _a.isRegistered(el); }) : [];
    const getNextFocusTarget = () => {
        var _a;
        if (disabled) {
            return null;
        }
        const focusables = getFocusables();
        const lastFocused = lastFocusedRef.current;
        return (_a = (lastFocused && focusables.includes(lastFocused) ? lastFocused : focusables[0])) !== null && _a !== void 0 ? _a : null;
    };
    useEffect(() => {
        var _a;
        (_a = navigationAPI.current) === null || _a === void 0 ? void 0 : _a.updateFocusTarget();
    });
    useImperativeHandle(ref, () => ({
        focusLast: () => { var _a; return (_a = getFocusables().pop()) === null || _a === void 0 ? void 0 : _a.focus(); },
    }));
    const onFocus = (event) => {
        var _a, _b;
        if (!nodeBelongs(rootRef.current, event.relatedTarget)) {
            onFocusEnter();
        }
        if (event.target instanceof HTMLElement && ((_a = navigationAPI.current) === null || _a === void 0 ? void 0 : _a.isRegistered(event.target))) {
            lastFocusedRef.current = event.target;
        }
        (_b = navigationAPI.current) === null || _b === void 0 ? void 0 : _b.updateFocusTarget();
    };
    const onKeyDown = (event) => {
        const focusables = getFocusables();
        const activeIndex = focusables.indexOf(event.target);
        if (activeIndex === -1) {
            return;
        }
        handleKey(event, {
            onInlineStart: () => {
                var _a;
                event.preventDefault();
                (_a = focusables[activeIndex - 1]) === null || _a === void 0 ? void 0 : _a.focus();
            },
            onInlineEnd: () => {
                event.preventDefault();
                if (activeIndex < focusables.length - 1) {
                    focusables[activeIndex + 1].focus();
                }
                else {
                    onNavigateToInput();
                }
            },
            onHome: () => {
                event.preventDefault();
                focusables[0].focus();
            },
            onEnd: () => {
                event.preventDefault();
                onNavigateToInput();
            },
        });
    };
    return (React.createElement("div", { ref: rootRef, className: styles['inline-tokens'], onFocus: onFocus, onKeyDown: onKeyDown },
        React.createElement(SingleTabStopNavigationProvider, { ref: navigationAPI, navigationActive: true, getNextFocusTarget: getNextFocusTarget },
            hiddenCount > 0 && (React.createElement(OverflowDropdown, { tokens: hiddenTokens, open: overflowOpen, disabled: disabled, readOnly: readOnly, expandToViewport: expandToViewport, tokenDismissLabel: tokenDismissLabel, tokenOverflowAriaLabel: tokenOverflowAriaLabel, onOpenChange: onOverflowOpenChange, onDismiss: index => onDismiss(index, 'overflow') })),
            visibleTokens.length > 0 && (React.createElement("ul", { className: clsx(styles['token-list'], testUtilStyles['token-list']) }, visibleTokens.map((token, i) => {
                const index = hiddenCount + i;
                return (React.createElement("li", { key: index, className: clsx(styles['token-list-item'], testUtilStyles['token-list-item']), "aria-setsize": tokens.length, "aria-posinset": index + 1, onKeyDown: event => {
                        const dismissToken = () => {
                            event.preventDefault();
                            if (!readOnly && !disabled) {
                                onDismiss(index, 'inline');
                            }
                        };
                        handleKey(event, { onBackspace: dismissToken, onDelete: dismissToken });
                    } },
                    React.createElement(InternalToken, { label: token.value, dismissLabel: tokenDismissLabel === null || tokenDismissLabel === void 0 ? void 0 : tokenDismissLabel(token.value), icon: token.icon, variant: "inline", disabled: disabled, readOnly: readOnly, onDismiss: () => onDismiss(index, 'inline') })));
            }))))));
});
//# sourceMappingURL=inline-tokens.js.map